import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { fmtDate, kycBadge } from "../../lib/format";
import AppLayout, { PageLoader } from "../../components/AppLayout";
import { Badge } from "../../components/ui/badge";

const STATUSES = ["Pending", "Submitted", "Under Review", "Verified", "Rejected"];

export default function AdminKyc() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/kyc").then((r) => setRows(r.data)).catch((e) => setError(apiError(e)));
  useEffect(() => { load(); }, []);

  const update = async (investorId, status) => {
    try {
      await api.patch(`/admin/kyc/${investorId}`, { kyc_status: status });
      toast.success("KYC status updated — propagated to all portals");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (error) return <AppLayout title="KYC"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!rows) return <AppLayout title="KYC"><PageLoader /></AppLayout>;

  return (
    <AppLayout title="KYC Management" subtitle="Review and update client KYC verification status.">
      <div className="fade-up space-y-4" data-testid="kyc-list">
        {rows.map((r) => (
          <div key={r.investor_id} className="glass-card p-6" data-testid={`kyc-row-${r.investor_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-heading font-semibold text-white">{r.full_name}</p>
                <p className="text-xs text-zinc-500 tnum mt-0.5">
                  {r.investor_code}{r.pan ? ` · PAN ${r.pan}` : ""}{r.kyc_verified_at ? ` · Verified ${fmtDate(r.kyc_verified_at)}` : ""}
                </p>
                <div className="flex items-center gap-2.5 mt-2 max-w-[220px]">
                  <div className="h-1.5 rounded-full bg-white/[0.06] flex-1 overflow-hidden">
                    <div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${r.kyc_completion ?? 0}%` }} />
                  </div>
                  <span className="tnum text-[11px] text-emerald-300 font-semibold" data-testid={`kyc-completion-${r.investor_id}`}>{r.kyc_completion ?? 0}%</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className={`text-[10px] ${kycBadge(r.kyc_status)}`}>{r.kyc_status}</Badge>
                <select value={r.kyc_status} onChange={(e) => update(r.investor_id, e.target.value)}
                  data-testid={`kyc-update-${r.investor_id}`}
                  className="glass-input h-9 w-40 text-xs appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {r.records.map((k) => (
                <span key={k.id} className={`text-[11px] border rounded-full px-3 py-1 ${kycBadge(k.status)}`}>
                  {k.document_type}: {k.status}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
